import React from "react";
import { Box } from "@mui/material";

import ExpertRanking from "../../../videoProduction/components/ExpertRanking";
import { IconExpert } from "../../../../utils/icons";
import { EmptyPanel } from "../../../brand/projects/common/EmptyPanel";
import { PublicProject, PublicProjectPro } from "../OrderPublicPage";

interface Props {
    project: PublicProject;
    token: string;
    onRefresh: () => void;
    publicBase?: string;
}

/**
 * Adapt a PublicProjectPro to the shape ExpertRanking expects:
 * { userid: string, displayName, avatar, projects: [{ BrandProjectPro: { score, status } }] }
 */
function adaptPro(pro: any) {
    // The API now returns the full User model with through data at
    // pro.projects[0].BrandProjectPro — pass through as-is, just ensure userid is a string
    return {
        ...pro,
        userid: String(pro.userid),
    };
}

/**
 * Adapt the public project shape to the minimal shape ExpertRanking reads from
 * project.briefs[0] for the general info section.
 */
function adaptProject(project: PublicProject) {
    const brief = project.brief;
    return {
        id: project.id,
        title: project.title,
        briefs: brief
            ? [
                  {
                      id: brief.id,
                      ...(brief.intakeForm || {}),
                      ...(brief.questions || {})
                  }
              ]
            : []
    };
}

export function PublicExpertSelectionSection({ project, token, onRefresh, publicBase = "brand-orders" }: Props) {
    const pros = project.pros ?? [];

    if (pros.length === 0) {
        return (
            <Box className="flex justify-center items-center h-full my-12 text-accent">
                <EmptyPanel
                    // `stroke` is an SVG attribute (no var()); the token rides in on currentColor.
                    icon={<IconExpert stroke="currentColor" />}
                    showSpacing
                    text="No experts yet. You will be notified when experts are available for selection."
                />
            </Box>
        );
    }

    const adaptedExperts = pros.map(adaptPro);
    const adaptedProject = adaptProject(project);

    return (
        <ExpertRanking
            experts={adaptedExperts}
            projectId={String(project.id)}
            brandId=""
            onUpdate={onRefresh}
            projectStatus={project.status}
            project={adaptedProject}
            role=""
            loading={false}
            publicToken={token}
            publicBase={publicBase}
        />
    );
}
